import { SafeHydrate } from "@/SafeHydrate";
import { useState, useRef, useCallback } from "react";
import { Move, MapPin, Calendar, Phone, ArrowRight, ArrowLeftRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 *  10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

/* ==========================================================================
   BEFORE/AFTER PAIRS — UPDATE IMAGE URLS AFTER UPLOAD TO 10WEB
   ==========================================================================
   Recommended size: 1200×800 WebP @ 82 quality (~100KB each)
   Aspect ratio: 3:2 — matched pair before & after for clean comparison
   ========================================================================== */

const PAIRS = [{
  before: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/PHOTO-2026-06-03-19-57-37.webp",
  after: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/PHOTO-2026-06-03-15-31-16.webp",
  title: "Faded → Monument ColorBond",
  suburb: "Bondi Junction",
  date: "Spring 2025",
  work: "Sectional door replacement. Original 1990s door swapped for B&D Panelift Icon in Monument ColorBond, with motor and tracks renewed.",
  pill: "Sectional upgrade",
  featured: true
}, {
  before: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/PHOTO-2026-03-23-18-45-10.webp",
  after: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/PHOTO-2026-04-13-20-04-40.webp",
  title: "Dented panel → Like new",
  suburb: "Parramatta",
  date: "Summer 2025",
  work: "Panel replacement after a car strike. Top 3 sections matched, painted, and refitted to the original frame.",
  pill: "Panel restore",
  featured: false
}, {
  before: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_0034-scaled.webp",
  after: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/IMG_0037-scaled.webp",
  title: "Salt-rusted → Marine-spec",
  suburb: "Cronulla",
  date: "Autumn 2025",
  work: "Coastal corrosion replacement. Centurion ThermaShield with marine-grade hardware, EPDM seals, and salt-resistant spring kit.",
  pill: "Coastal install",
  featured: false
}, {
  before: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/door-768x256-1.jpg",
  after: "https://goldgaragedoor.com.au/wp-content/uploads/2026/06/Resized_20260414_143540.jpeg",
  title: "Manual lift → Smart opener",
  suburb: "Drummoyne",
  date: "Winter 2025",
  work: "Insulated upgrade with Wi-Fi smart opener. Steel-Line Therma-Lux with Chamberlain MyQ commissioning and HomeLink sync.",
  pill: "Smart upgrade",
  featured: false
}];
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";

/* ==========================================================================
   BeforeAfterSlider — interactive draggable comparison component
   ========================================================================== */

interface BeforeAfterSliderProps {
  before: string;
  after: string;
  beforeAlt: string;
  afterAlt: string;
  height?: number;
}
function BeforeAfterSlider({
  before,
  after,
  beforeAlt,
  afterAlt,
  height = 380
}: BeforeAfterSliderProps) {
  const [pos, setPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const updateFromEvent = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const clamped = Math.max(0, Math.min(1, ratio));
    setPos(clamped * 100);
  }, []);
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(true);
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    updateFromEvent(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    updateFromEvent(e.clientX);
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(false);
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch (err) {
      /* ignore */
    }
  };
  return <div ref={containerRef} className="relative overflow-hidden rounded-xl select-none" style={{
    height: `${height}px`,
    backgroundColor: "#0e1a36",
    cursor: isDragging ? "grabbing" : "grab",
    touchAction: "none"
  }} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerCancel={handlePointerUp}>
      {/* Before image — full visible */}
      <img src={before} alt={beforeAlt} className="absolute inset-0 w-full h-full object-cover pointer-events-none" loading="lazy" draggable={false} />

      {/* After image — clipped */}
      <div className="absolute inset-0 pointer-events-none" style={{
      clipPath: `inset(0 0 0 ${pos}%)`
    }}>
        <img src={after} alt={afterAlt} className="w-full h-full object-cover" loading="lazy" draggable={false} />
      </div>

      {/* Before label */}
      <div className="absolute top-4 left-4 pointer-events-none" style={{
      padding: "6px 12px",
      borderRadius: "999px",
      backgroundColor: "rgba(8,20,56,0.85)",
      backdropFilter: "blur(6px)",
      fontFamily: FONT_HEAD,
      fontSize: "10.5px",
      fontWeight: 800,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: "rgba(255,255,255,0.85)",
      border: "1px solid rgba(255,255,255,0.15)"
    }}>
        Before
      </div>

      {/* After label */}
      <div className="absolute top-4 right-4 pointer-events-none" style={{
      padding: "6px 12px",
      borderRadius: "999px",
      background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
      fontFamily: FONT_HEAD,
      fontSize: "10.5px",
      fontWeight: 800,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: NAVY
    }}>
        After
      </div>

      {/* Slider line + handle */}
      <div className="absolute top-0 bottom-0 pointer-events-none" style={{
      left: `${pos}%`,
      width: "2px",
      marginLeft: "-1px",
      background: `linear-gradient(180deg, ${GOLD}AA 0%, ${GOLD} 50%, ${GOLD}AA 100%)`,
      boxShadow: `0 0 16px ${GOLD}55`
    }} />

      {/* Handle circle */}
      <div className="absolute top-1/2 pointer-events-none" style={{
      left: `${pos}%`,
      transform: "translate(-50%, -50%)",
      width: 48,
      height: 48,
      borderRadius: "50%",
      background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: NAVY,
      boxShadow: `0 6px 20px ${GOLD}66, 0 0 0 4px rgba(8,20,56,0.4)`,
      transition: isDragging ? "none" : "transform 0.15s ease"
    }}>
        <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
          <ArrowLeftRight size={20} color={NAVY} strokeWidth={3} />
        </span>
      </div>

      {/* Drag hint (only when not dragged yet) */}
      {pos > 48 && pos < 52 && !isDragging && <div className="absolute pointer-events-none" style={{
      bottom: "16px",
      left: "50%",
      transform: "translateX(-50%)",
      padding: "5px 12px",
      borderRadius: "999px",
      backgroundColor: "rgba(8,20,56,0.75)",
      backdropFilter: "blur(6px)",
      fontFamily: FONT_HEAD,
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      color: GOLD,
      border: `1px solid ${GOLD}33`,
      display: "flex",
      alignItems: "center",
      gap: "6px"
    }}>
          <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
            <Move size={10} color={GOLD} strokeWidth={2.5} />
          </span>
          Drag to compare
        </div>}
    </div>;
}

/* ==========================================================================
   Main Section
   ========================================================================== */

export default function GalleryBeforeAfter() {
  return <section id="before-after" className="relative overflow-hidden scroll-mt-24" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 20% 0%, ${NAVY_LIGHT}55 0%, ${NAVY} 50%), radial-gradient(circle at 85% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        03
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              03 · Before &amp; After
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            Drag the slider.
            <br />
            <span style={{
            color: GOLD
          }}>Watch the door come back to life</span>.
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Four Sydney homes, four very different starting points. Each
            slider is a real install — drag the gold handle left and right
            to compare day one to day done.
          </p>
        </div>

        {/* Featured large slider */}
        {PAIRS.filter(p => p.featured).map(pair => <article key={pair.title} className="mb-6 relative rounded-2xl overflow-hidden" style={{
        backgroundColor: `${NAVY_DEEP}AA`,
        border: `2px solid ${GOLD}55`,
        boxShadow: `0 30px 80px rgba(0,0,0,0.45), 0 0 0 1px ${GOLD}33`
      }}>
            {/* Top gold edge */}
            <div aria-hidden className="absolute top-0 left-0 right-0 h-1" style={{
          background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
          zIndex: 2
        }} />

            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-0">
              {/* Slider */}
              <div className="p-5 md:p-6">
                <BeforeAfterSlider before={pair.before} after={pair.after} beforeAlt={`Before: ${pair.title} in ${pair.suburb}, Sydney`} afterAlt={`After: ${pair.title} completed by Gold Garage Door in ${pair.suburb}, Sydney`} height={460} />
              </div>

              {/* Info */}
              <div className="p-7 md:p-9 flex flex-col justify-center border-t lg:border-t-0 lg:border-l relative" style={{
            borderColor: "rgba(255,255,255,0.08)"
          }}>
                <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full self-start" style={{
              backgroundColor: `${GOLD}20`,
              border: `1px solid ${GOLD}55`
            }}>
                  <div style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: GOLD
              }} />
                  <span style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 800,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: GOLD
              }}>
                    Featured · {pair.pill}
                  </span>
                </div>

                <h3 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "clamp(1.5rem, 2.4vw, 1.95rem)",
              lineHeight: 1.15,
              color: "#ffffff",
              letterSpacing: "-0.025em",
              marginBottom: "16px"
            }}>
                  {pair.title}
                </h3>

                <div className="flex flex-wrap items-center gap-4 mb-5">
                  <div className="flex items-center gap-1.5">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <MapPin size={13} color={GOLD} strokeWidth={2.5} />
                    </span>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.88)"
                }}>
                      {pair.suburb}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <SafeHydrate><Calendar size={13} color={GOLD} strokeWidth={2.5} /></SafeHydrate>
                    </span>
                    <span style={{
                  fontFamily: FONT_BODY,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.7)"
                }}>
                      {pair.date}
                    </span>
                  </div>
                </div>

                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "14.5px",
              lineHeight: 1.65,
              color: "rgba(255,255,255,0.72)",
              marginBottom: "22px"
            }}>
                  {pair.work}
                </p>

                <Link href="#contact-us" className="inline-flex items-center gap-2 px-5 py-3 rounded-full transition-all hover:scale-[1.02] self-start" style={{
              backgroundColor: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontWeight: 700,
              fontSize: "13px",
              boxShadow: `0 6px 18px ${GOLD}33`
            }}>
                  Quote one like this
                  <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                    <ArrowRight size={13} strokeWidth={2.5} />
                  </span>
                </Link>
              </div>
            </div>
          </article>)}

        {/* Smaller pairs grid */}
        <div className="grid md:grid-cols-3 gap-5">
          {PAIRS.filter(p => !p.featured).map(pair => <article key={pair.title} className="relative rounded-2xl overflow-hidden" style={{
          backgroundColor: `${NAVY_LIGHT}55`,
          border: `1px solid rgba(255,255,255,0.08)`
        }}>
              <div className="p-3.5">
                <BeforeAfterSlider before={pair.before} after={pair.after} beforeAlt={`Before: ${pair.title} in ${pair.suburb}, Sydney`} afterAlt={`After: ${pair.title} completed by Gold Garage Door in ${pair.suburb}, Sydney`} height={260} />
              </div>

              <div className="px-6 pb-6 pt-1">
                <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "10px",
              fontWeight: 700,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: "6px"
            }}>
                  {pair.pill}
                </div>
                <h4 style={{
              fontFamily: FONT_HEAD,
              fontWeight: 800,
              fontSize: "1.05rem",
              lineHeight: 1.2,
              color: "#ffffff",
              letterSpacing: "-0.015em",
              marginBottom: "10px"
            }}>
                  {pair.title}
                </h4>

                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-1">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <MapPin size={11} color="rgba(255,255,255,0.55)" strokeWidth={2.5} />
                    </span>
                    <span style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.75)"
                }}>
                      {pair.suburb}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                      <SafeHydrate><Calendar size={11} color="rgba(255,255,255,0.55)" strokeWidth={2.5} /></SafeHydrate>
                    </span>
                    <span style={{
                  fontFamily: FONT_BODY,
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.6)"
                }}>
                      {pair.date}
                    </span>
                  </div>
                </div>

                <p style={{
              fontFamily: FONT_BODY,
              fontSize: "13px",
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.66)"
            }}>
                  {pair.work}
                </p>
              </div>
            </article>)}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "15px",
          lineHeight: 1.6,
          color: "rgba(255,255,255,0.7)",
          maxWidth: "560px"
        }}>
            <span style={{
            color: GOLD,
            fontFamily: FONT_HEAD,
            fontWeight: 700
          }}>
              Got a "before" you'd love to see fixed?
            </span>{" "}
            Send us a photo on WhatsApp — we'll quote what an "after" looks
            like for your home, flat-price, before we leave the yard.
          </p>

          <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: GOLD,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "14px",
          boxShadow: `0 8px 24px ${GOLD}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={16} strokeWidth={2.5} />
            </span>
            Quote my before-and-after
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={14} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}